import { ContentArea } from '../types';
import {
  contentReferenceClass,
  externalReferenceClass,
  getNodeIgnoreFiberNodes,
  isFiberNode,
  getIgnoreNode,
  isTextNode,
} from './nodes';

/**
 * Ensures the node path only changes when the content itself changes and not when marks/links, etc., are rendered in the content.
 * All elements that belong to marks/links need to be ignored. This function returns the next sibling on the same level in the DOM tree
 * that does not belong to a mark or link.
 *
 * @param {Node} node - The node from which the path walking is started.
 * @returns {Node} The next sibling unrelated to marks or links.
 *
 * ![Find startnode example](../../../../docs/images/getNextElementIgnoreMarksAndLinks.png)
 */
const getNextElementIgnoreMarksAndLinks = (node: Node): Node => {
  const nextSibling = getNodeIgnoreFiberNodes(node, 'nextSibling');
  if (!nextSibling) {
    return node;
  }

  if (getIgnoreNode(nextSibling) || isTextNode(nextSibling)) {
    return getNextElementIgnoreMarksAndLinks(nextSibling);
  }

  return nextSibling;
};

/**
 * Utilizes the node path to find the HTML element that is the start or end node of the range.
 *
 * @param {Document} doc - The document the node belongs to.
 * @param {string} containerId - The ID of the container the node is in.
 * @param {number[]} nodePath - The path that leads to the desired node.
 * @returns {Node | null} The node that sits on the given path.
 *
 *  ![Path example](../../../../docs/images/path.png)
 */
const findContainer = (
  doc: Document,
  containerId: string,
  nodePath: number[],
): Node | null => {
  let currentNode: Node | null = doc.getElementById(containerId) as Node;

  // For each level find the node to walk past on the path down to the node
  nodePath.forEach((number) => {
    if (!currentNode) {
      return;
    }

    const currentEl = currentNode as Element;
    let hasMarksOrLinks = false;

    try {
      hasMarksOrLinks =
        currentEl.getElementsByTagName('mark').length > 0 ||
        currentEl.getElementsByTagName('a').length > 0 ||
        !!currentEl.querySelector(
          `.${contentReferenceClass}, .${externalReferenceClass}`,
        );
    } catch {
      hasMarksOrLinks = true;
    }

    if (hasMarksOrLinks) {
      currentNode = getNodeIgnoreFiberNodes(currentNode, 'firstChild');

      for (let i = 1; i < number; i += 1) {
        if (!currentNode) {
          return;
        }

        if (getIgnoreNode(currentNode) || isTextNode(currentNode)) {
          currentNode = getNextElementIgnoreMarksAndLinks(currentNode);
        } else {
          currentNode = getNodeIgnoreFiberNodes(currentNode, 'nextSibling');
        }
      }
    } else {
      const currentChildNodes = Array.from(
        currentNode?.childNodes || [],
      ).filter((node) => !isFiberNode(node));
      currentNode = currentChildNodes[number - 1];
    }
  });

  return currentNode || null;
};

/**
 * Because marks and links are dynamically rendered in the content, they need to be ignored for path and offset calculation.
 * However, while the nodes themselves need to be ignored (see {@link getNextElementIgnoreMarksAndLinks}), their text content needs to be considered for the offset.
 *
 * @param {Node} node - The node on which the calculation starts.
 * @param {number} offset - The current offset.
 * @returns {{n: Node, o: number}} The resulting node and offset.
 *
 * ![Addition Example](../../../../docs/images/offsetCalculation.png)
 */
const findOffsetAndUpdateNode = (
  node: Node,
  offset: number,
): { node: Node; offset: number } | undefined => {
  const nodeTextLength = node?.textContent?.length || 0;

  if (nodeTextLength >= offset) {
    if (isTextNode(node)) {
      return { node, offset };
    }
    return { node: node.firstChild || node, offset };
  }

  const nextNode = getNodeIgnoreFiberNodes(node, 'nextSibling');
  const newOffset = offset - nodeTextLength;

  return nextNode ? findOffsetAndUpdateNode(nextNode, newOffset) : undefined;
};

/**
 * Converts the data from the database to a range inside the HTML document.
 *
 * @param {Document} doc - The document the element is in (could be the actual document of the application or a calculation document).
 * @param {string} containerId - The ID of the HTML element that contains the full HTML content from the database.
 * @param {ContentArea} contentArea - The `ContentArea` (converted from Mark/Link) from the database.
 * @returns {Range | undefined} The range; if `undefined` is returned, usually something went wrong.
 */
const convertToRange = (
  doc: Document,
  containerId: string,
  contentArea: ContentArea,
): Range | undefined => {
  let startDataJSON;
  let endDataJSON;

  try {
    startDataJSON = JSON.parse(contentArea.startNodeData || '');
    endDataJSON = JSON.parse(contentArea.endNodeData || '');
  } catch {
    return undefined;
  }

  const startNode = findContainer(
    doc,
    containerId,
    [...startDataJSON.nodePath].reverse(),
  );
  const endNode = findContainer(
    doc,
    containerId,
    [...endDataJSON.nodePath].reverse(),
  );
  if (!startNode || !endNode) {
    return undefined;
  }

  const startData = findOffsetAndUpdateNode(startNode, startDataJSON.offset);
  const endData = findOffsetAndUpdateNode(endNode, endDataJSON.offset);
  if (!startData || !endData) {
    return undefined;
  }

  const range: Range = document.createRange();
  try {
    range.setStart(startData.node, startData.offset);
    range.setEnd(endData.node, endData.offset);
  } catch {
    return undefined;
  }

  if (contentArea.patternString !== range.toString()) {
    return undefined;
  }

  return range;
};

export default convertToRange;
